/* eslint-disable max-lines */
import {
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  Control,
  Controller
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import cn from 'classnames';
import { Check, Shield } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input';
import SelectInput from '@components/atoms/ui-kit/select-input';
import PricePlanTierEditor from './price-plan-tier-editor';
import PricePlanOverview from './price-plan-overview';
import { WizardFormData, FEATURE_OPTIONS } from './create-plan-wizard-types';
import { PlanType } from '@utils/types/back-office/update-plan-bo';
import { Elements } from '@utils/providers/theme-context';

interface PricePlan {
  id: number;
  name: string;
  type?: string;
}

interface Props {
  watchedValues: WizardFormData;
  planType: PlanType;
  setValue: UseFormSetValue<WizardFormData>;
  register: UseFormRegister<WizardFormData>;
  control: Control<WizardFormData>;
  errors: FieldErrors<WizardFormData>;
  pricePlans: PricePlan[];
}

function PricingModeToggle({
  activeMode,
  modes,
  onSelect,
  getColour
}: {
  activeMode: string;
  modes: { value: string; label: string }[];
  onSelect: (mode: string) => void;
  getColour: (e: Elements) => string;
}) {
  return (
    <div
      className="inline-flex rounded-2xl p-0.5"
      style={{ backgroundColor: getColour('secondLayer') }}
    >
      {modes.map(m => (
        <button
          key={m.value}
          type="button"
          onClick={() => onSelect(m.value)}
          className={cn(
            'px-3 py-1.5 rounded-2xl text-xs font-medium transition-all',
            activeMode === m.value ? 'bg-blue-500 text-white' : ''
          )}
          style={
            activeMode !== m.value
              ? { color: getColour('inactiveText') }
              : undefined
          }
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

export function WizardStep2FeaturesPricing({
  watchedValues,
  planType,
  setValue,
  register,
  control,
  errors,
  pricePlans
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <div className="space-y-4">
      {/* Feature Toggles */}
      <div
        className="p-4 rounded-2xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <h4
          className="text-sm font-medium mb-1"
          style={{ color: getColour('normalText') }}
        >
          {t('enabled-features')}
        </h4>
        <p
          className="text-xs mb-3"
          style={{ color: getColour('inactiveText') }}
        >
          {t('enabled-features-description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-1.5">
          {FEATURE_OPTIONS.map(feature => {
            const isEnabled = watchedValues[feature.key];
            const Icon = feature.icon;

            return (
              <button
                key={feature.key}
                type="button"
                onClick={() => setValue(feature.key, !isEnabled)}
                className="relative p-2 rounded-2xl border text-left flex align-middle transition-all"
                style={{
                  borderColor: isEnabled ? feature.color : getColour('border'),
                  backgroundColor: isEnabled
                    ? feature.bgColor
                    : getColour('secondLayer')
                }}
              >
                {isEnabled && (
                  <div
                    className="absolute top-1.5 right-1.5 h-3.5 w-3.5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: feature.color }}
                  >
                    <Check className="h-2 w-2 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div
                    className="h-5 w-5 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: feature.bgColor }}
                  >
                    <Icon
                      className="h-3 w-3"
                      style={{ color: feature.color }}
                    />
                  </div>
                  <span
                    className="text-[11px] font-medium truncate"
                    style={{ color: getColour('normalText') }}
                  >
                    {t(feature.label)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Plan Cost */}
      <div
        className="p-4 rounded-2xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('plan-cost')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {planType === 'Bundle' && (
            <Input
              label={t('monthly-cost')}
              type="number"
              size="small"
              {...register('monthly_cost', { valueAsNumber: true })}
              placeholder="0.00"
            />
          )}
          {planType === 'Perpetual' && (
            <Input
              label={t('cost')}
              type="number"
              size="small"
              {...register('cost', { valueAsNumber: true })}
              placeholder="0.00"
            />
          )}
          {planType === 'Pay as you Go' && (
            <Input
              label={t('minimal-invoice')}
              type="number"
              size="small"
              {...register('minimal_invoice', { valueAsNumber: true })}
              placeholder="0.00"
            />
          )}
        </div>
      </div>

      {/* STT Pricing */}
      {watchedValues.batch_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-3">
            <h4
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('stt-pricing')}
            </h4>
            <PricingModeToggle
              activeMode={watchedValues.stt_pricing_mode}
              modes={[
                { value: 'existing', label: t('use-existing-price-plan') },
                { value: 'inline', label: t('create-inline-tiers') }
              ]}
              onSelect={mode => {
                setValue('stt_pricing_mode', mode as 'existing' | 'inline');
                if (
                  mode === 'inline' &&
                  watchedValues.stt_pricing_tiers.length === 0
                ) {
                  setValue('stt_pricing_tiers', [
                    { start: 0, end: null, price_per_unit: '0.00' }
                  ]);
                }
              }}
              getColour={getColour}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label={t('media-unit-size')}
              type="number"
              size="small"
              {...register('media_unit_size', { valueAsNumber: true })}
              placeholder="60"
            />
            <Input
              label={
                <>
                  {t('stt-limit')}
                  <span
                    className="ml-1 text-[10px] font-normal"
                    style={{ color: getColour('inactiveText') }}
                  >
                    ({t('fair-use-soft-cap')})
                  </span>
                </>
              }
              type="number"
              size="small"
              {...register('stt_limit', { valueAsNumber: true })}
              placeholder={t('unlimited')}
            />
            {watchedValues.stt_pricing_mode === 'existing' && (
              <Controller
                name="stt_pricing_plan"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    label={t('stt-price-plan')}
                    size="small"
                    options={pricePlans.map(p => ({
                      label: p.name,
                      value: String(p.id)
                    }))}
                    value={
                      field.value
                        ? {
                            label:
                              pricePlans.find(p => p.id === field.value)
                                ?.name || `Plan #${field.value}`,
                            value: String(field.value)
                          }
                        : null
                    }
                    onChange={opt =>
                      field.onChange(opt ? Number(opt.value) : undefined)
                    }
                    placeholder={t('select')}
                  />
                )}
              />
            )}
          </div>

          {watchedValues.stt_pricing_mode === 'existing' &&
            watchedValues.stt_pricing_plan != null && (
              <PricePlanOverview
                pricePlanId={watchedValues.stt_pricing_plan}
                unitLabel="min"
              />
            )}

          {watchedValues.stt_pricing_mode === 'inline' && (
            <div
              className="py-3 rounded-2xl"
              style={{ backgroundColor: getColour('baseLayer') }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <Input
                  label={t('price-plan-name')}
                  size="small"
                  {...register('stt_pricing_plan_name')}
                  placeholder={t('enter-price-plan-name')}
                />
              </div>
              <PricePlanTierEditor
                value={watchedValues.stt_pricing_tiers}
                onChange={tiers => setValue('stt_pricing_tiers', tiers)}
              />
            </div>
          )}
        </div>
      )}

      {/* Realtime */}
      {watchedValues.realtime_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('realtime')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label={t('max-concurrent-realtime-streams')}
              type="number"
              size="small"
              {...register('realtime_max_concurrent_streams', {
                setValueAs: v =>
                  v === '' || v === null || v === undefined
                    ? undefined
                    : Number(v)
              })}
              placeholder={t('unlimited')}
            />
            <Input
              label={t('allowed-realtime-languages')}
              size="small"
              {...register('realtime_languages_csv')}
              placeholder={t('realtime-languages-placeholder')}
            />
          </div>
        </div>
      )}

      {watchedValues.ai_summaries_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('ai-summaries')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label={t('monthly-limit')}
              type="number"
              size="small"
              {...register('ai_summaries_monthly_limit', {
                setValueAs: v =>
                  v === '' || v === null || v === undefined
                    ? undefined
                    : Number(v)
              })}
              placeholder={t('unlimited')}
            />
          </div>
        </div>
      )}

      {/* Alignment Pricing */}
      {watchedValues.alignment_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-3">
            <h4
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('alignment-pricing')}
            </h4>
            <PricingModeToggle
              activeMode={watchedValues.alignment_pricing_mode}
              modes={[
                { value: 'existing', label: t('use-existing-price-plan') },
                { value: 'inline', label: t('create-inline-tiers') }
              ]}
              onSelect={mode => {
                setValue(
                  'alignment_pricing_mode',
                  mode as 'existing' | 'inline'
                );
                if (
                  mode === 'inline' &&
                  watchedValues.alignment_pricing_tiers.length === 0
                ) {
                  setValue('alignment_pricing_tiers', [
                    { start: 0, end: null, price_per_unit: '0.00' }
                  ]);
                }
              }}
              getColour={getColour}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {watchedValues.alignment_pricing_mode === 'existing' && (
              <Controller
                name="alignment_pricing_plan"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    label={t('alignment-price-plan')}
                    size="small"
                    options={pricePlans.map(p => ({
                      label: p.name,
                      value: String(p.id)
                    }))}
                    value={
                      field.value
                        ? {
                            label:
                              pricePlans.find(p => p.id === field.value)
                                ?.name || `Plan #${field.value}`,
                            value: String(field.value)
                          }
                        : null
                    }
                    onChange={opt =>
                      field.onChange(opt ? Number(opt.value) : undefined)
                    }
                    placeholder={t('select')}
                  />
                )}
              />
            )}
          </div>

          {watchedValues.alignment_pricing_mode === 'existing' &&
            watchedValues.alignment_pricing_plan != null && (
              <PricePlanOverview
                pricePlanId={watchedValues.alignment_pricing_plan}
                unitLabel="min"
              />
            )}

          {watchedValues.alignment_pricing_mode === 'inline' && (
            <div
              className="py-3 rounded-2xl"
              style={{ backgroundColor: getColour('baseLayer') }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <Input
                  label={t('price-plan-name')}
                  size="small"
                  {...register('alignment_pricing_plan_name')}
                  placeholder={t('enter-price-plan-name')}
                />
              </div>
              <PricePlanTierEditor
                value={watchedValues.alignment_pricing_tiers}
                onChange={tiers => setValue('alignment_pricing_tiers', tiers)}
              />
            </div>
          )}
        </div>
      )}

      {/* Translation Pricing */}
      {watchedValues.translation_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-3">
            <h4
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('translation-pricing')}
            </h4>
            <PricingModeToggle
              activeMode={watchedValues.translation_pricing_mode}
              modes={[
                { value: 'flat', label: t('flat-pricing') },
                { value: 'existing', label: t('use-existing-price-plan') },
                { value: 'inline', label: t('create-inline-tiers') }
              ]}
              onSelect={mode => {
                setValue(
                  'translation_pricing_mode',
                  mode as 'flat' | 'existing' | 'inline'
                );
                if (
                  mode === 'inline' &&
                  watchedValues.translation_pricing_tiers.length === 0
                ) {
                  setValue('translation_pricing_tiers', [
                    { start: 0, end: null, price_per_unit: '0.0001' }
                  ]);
                }
              }}
              getColour={getColour}
            />
          </div>

          {watchedValues.translation_pricing_mode === 'flat' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label={t('cost-per-unit')}
                type="number"
                size="small"
                {...register('translation_cost', { valueAsNumber: true })}
                placeholder="0.0001"
              />
              <Input
                label={t('characters-per-unit')}
                type="number"
                size="small"
                {...register('translation_characters', {
                  valueAsNumber: true
                })}
                placeholder="1000"
              />
              <Input
                label={t('translation-character-limit')}
                type="number"
                size="small"
                {...register('translation_character_limit', {
                  valueAsNumber: true
                })}
                placeholder={t('optional')}
              />
            </div>
          )}

          {watchedValues.translation_pricing_mode === 'existing' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Controller
                name="translation_pricing_plan"
                control={control}
                render={({ field }) => {
                  const translationPlans = pricePlans.filter(
                    p => p.type === 'TRANSLATION'
                  );
                  return (
                    <SelectInput
                      label={t('translation-price-plan')}
                      size="small"
                      options={translationPlans.map(p => ({
                        label: p.name,
                        value: String(p.id)
                      }))}
                      value={
                        field.value
                          ? {
                              label:
                                translationPlans.find(p => p.id === field.value)
                                  ?.name || `Plan #${field.value}`,
                              value: String(field.value)
                            }
                          : null
                      }
                      onChange={opt =>
                        field.onChange(opt ? Number(opt.value) : undefined)
                      }
                      placeholder={t('select')}
                    />
                  );
                }}
              />
              <Input
                label={t('translation-character-limit')}
                type="number"
                size="small"
                {...register('translation_character_limit', {
                  valueAsNumber: true
                })}
                placeholder={t('optional')}
              />
              {watchedValues.translation_pricing_plan != null && (
                <div className="md:col-span-2">
                  <PricePlanOverview
                    pricePlanId={watchedValues.translation_pricing_plan}
                    unitLabel="char"
                  />
                </div>
              )}
            </div>
          )}

          {watchedValues.translation_pricing_mode === 'inline' && (
            <div
              className="py-3 rounded-2xl"
              style={{ backgroundColor: getColour('baseLayer') }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <Input
                  label={t('price-plan-name')}
                  size="small"
                  {...register('translation_pricing_plan_name')}
                  placeholder={t('enter-price-plan-name')}
                />
                <Input
                  label={t('translation-character-limit')}
                  type="number"
                  size="small"
                  {...register('translation_character_limit', {
                    valueAsNumber: true
                  })}
                  placeholder={t('optional')}
                />
              </div>
              <PricePlanTierEditor
                value={watchedValues.translation_pricing_tiers}
                onChange={tiers => setValue('translation_pricing_tiers', tiers)}
              />
            </div>
          )}
        </div>
      )}

      {/* Exceedance Email Alerts */}
      <div
        className="p-4 rounded-2xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('exceedance-email-alerts')}
        </h4>
        <div className="grid grid-cols-1 gap-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded-2xl border-hairline"
              {...register('send_exceedance_email')}
            />
            <span className="text-sm font-medium">
              {t('send-exceedance-email')}
            </span>
          </label>
          {watchedValues.send_exceedance_email && (
            <Input
              label={t('exceedance-email-recipients')}
              type="text"
              size="small"
              {...register('exceedance_email_recipients', {
                required: watchedValues.send_exceedance_email
                  ? t('exceedance-email-recipients-required')
                  : false
              })}
              placeholder="email1@example.com, email2@example.com"
              error={
                typeof errors.exceedance_email_recipients?.message === 'string'
                  ? errors.exceedance_email_recipients.message
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Training Pricing */}
      {watchedValues.automatic_training_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('training-pricing')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label={t('unit-cost')}
              type="number"
              size="small"
              {...register('automatic_training_unit_cost', {
                valueAsNumber: true
              })}
              placeholder="0.00"
            />
            <Input
              label={t('monthly-cost')}
              type="number"
              size="small"
              {...register('automatic_training_monthly_cost', {
                valueAsNumber: true
              })}
              placeholder="0.00"
            />
          </div>
        </div>
      )}

      {/* Template Pricing */}
      {watchedValues.templates_exported_enabled && (
        <div
          className="p-4 rounded-2xl"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('template-pricing')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label={t('export-cost')}
              type="number"
              size="small"
              {...register('stt_templates_exported_cost', {
                valueAsNumber: true
              })}
              placeholder="0.00"
            />
          </div>
        </div>
      )}

      {/* Fair Use */}
      <div
        className="p-4 rounded-2xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <h4
          className="text-sm font-medium mb-1"
          style={{ color: getColour('normalText') }}
        >
          {t('fair-use')}
        </h4>
        <p
          className="text-xs mb-3"
          style={{ color: getColour('inactiveText') }}
        >
          {t('fair-use-description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setValue('is_fair_use', !watchedValues.is_fair_use)}
            className="relative p-3 rounded-2xl border-2 text-left transition-all"
            style={{
              borderColor: watchedValues.is_fair_use
                ? 'rgba(20, 184, 166, 0.7)'
                : getColour('border'),
              backgroundColor: watchedValues.is_fair_use
                ? 'rgba(20, 184, 166, 0.08)'
                : getColour('secondLayer')
            }}
          >
            {watchedValues.is_fair_use && (
              <div
                className="absolute top-2 right-2 h-4 w-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.9)' }}
              >
                <Check className="h-2.5 w-2.5 text-white" />
              </div>
            )}
            <div
              className="h-7 w-7 rounded-2xl flex items-center justify-center mb-2"
              style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
            >
              <Shield
                className="h-3.5 w-3.5"
                style={{ color: 'rgba(20, 184, 166, 0.9)' }}
              />
            </div>
            <span
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('enable-fair-use-mode')}
            </span>
            <span
              className="text-[10px] block mt-0.5"
              style={{ color: getColour('inactiveText') }}
            >
              {t('fair-use-mode-description')}
            </span>
          </button>

          <div
            className="md:col-span-2 p-3 rounded-2xl text-xs space-y-1 flex flex-col justify-center"
            style={{
              backgroundColor: getColour('secondLayer'),
              color: getColour('inactiveText')
            }}
          >
            <p>
              <span
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('when-enabled')}:
              </span>{' '}
              {t('fair-use-soft-limit-explanation')}
            </p>
            <p>
              <span
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('when-disabled')}:
              </span>{' '}
              {t('fair-use-hard-limit-explanation')}
            </p>
          </div>
        </div>

        {watchedValues.is_fair_use && (
          <div
            className="mt-3 p-3 rounded-2xl"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Input
                  label={
                    <>
                      {t('rate-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('requests-per-minute')})
                      </span>
                    </>
                  }
                  type="number"
                  size="small"
                  {...register('fair_use_rate_limit', { valueAsNumber: true })}
                  placeholder={t('unlimited')}
                />
                <p
                  className="text-[10px] mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('rate-limit-note')}
                </p>
              </div>
              <div>
                <Input
                  label={
                    <>
                      {t('training-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('per-period')})
                      </span>
                    </>
                  }
                  type="number"
                  size="small"
                  {...register('automatic_training_unit_limit', {
                    valueAsNumber: true
                  })}
                  placeholder={t('unlimited')}
                />
              </div>
              <div>
                <Input
                  label={
                    <>
                      {t('hosting-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('models')})
                      </span>
                    </>
                  }
                  type="number"
                  size="small"
                  {...register('automatic_training_hosting_limit', {
                    valueAsNumber: true
                  })}
                  placeholder={t('unlimited')}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom model training */}
      <div
        className="p-4 rounded-2xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <h4
          className="text-sm font-medium mb-1"
          style={{ color: getColour('normalText') }}
        >
          {t('custom-models')}
        </h4>
        <div className="mt-2">
          <Input
            label={
              <>
                {t('custom-models-per-year')}
                <span
                  className="ml-1 text-[10px] font-normal"
                  style={{ color: getColour('inactiveText') }}
                >
                  ({t('per-year')})
                </span>
              </>
            }
            type="number"
            size="small"
            {...register('custom_model_yearly_limit', {
              valueAsNumber: true
            })}
            placeholder={t('unlimited')}
          />
          <p
            className="text-[10px] mt-1"
            style={{ color: getColour('inactiveText') }}
          >
            {t('custom-models-per-year-description')}
          </p>
        </div>
      </div>
    </div>
  );
}
